/****************************************************
 * Module Name:  packetHistoryModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Avatar, Box, Button, Chip, Container, FormControlLabel, FormGroup, Switch, Tooltip } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { useEffect } from "react";
import KapanCreationProxy from "../../utility/proxy/manager/manufacturingProxy/kapanCreationProxy";
import Modal from "react-bootstrap/Modal";
import Constants from "../../utility/constants";
import { v4 as uuidv4 } from "uuid";
import RemarkModal from "./remarkModal";
import StoneDetailModal from "../common/stoneDetailModal";
import { FaPen } from 'react-icons/fa'
import "./../../style/modalstyle.css";
import logo from '../../images/pansuriya.jpg'
import CommonSnackbar from "./commonSnackBar";
import EvaluationModal from "../common/evaluationModal";
import BucketDetailModal from "../mixDiamondSales/bucketDetailModal";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";

const PacketHistoryModal = ({ kapanId, packetId, close, show }) => {
    const [data, setData] = useState([]);
    const [showRemarkModal, setShowRemarkModal] = useState(false);
    const [showInlineRemark, setShowInlineRemark] = useState(true);
    const [detailLoader, setDetailLoader] = useState(false);
    const [sourcedata, setSourcedataData] = useState([]);
    const [showEvaluationModal, setShowEvaluationModal] = useState(false);
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [stoneId, setStoneId] = useState("");
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [showBucketDetailModal, setShowBucketDetailModal] = useState(false);
    const [bucketId, setBucketId] = useState([]);
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [columnNames, setColumnNames] = useState([]);

    useEffect(() => {
        handleSearch(kapanId, packetId);
    }, []);

    const handleSearch = (kapanId, packetId) => {
        setDetailLoader(true);
        KapanCreationProxy.GetSingleKapanDetail(
            kapanId,
            packetId,
            (response) => {
                if (response) {
                    let data = response
                    for (let i = 0; i < data.length; i++) {
                        data[i].id = uuidv4();
                    }
                    let prevRow = null;
                    data.map((iRow) => {
                        if (prevRow === null) {
                            prevRow = iRow
                            // Considering the 1st row as the previous row, we wont be showing any color change in this
                            iRow.prevRow = iRow;
                        } else {
                            iRow.prevRow = prevRow;
                            prevRow = iRow;
                        }
                        return true;
                    });
                    const uniqueFilterValues = {
                        updatedby: Utility.getDistinctOptions(response, "updatedbyid"),
                        user: Utility.getDistinctOptions(response, "username"),
                        department: Utility.getDistinctOptions(response, "department_name"),
                        operation: Utility.getDistinctOptions(response, "operation"),
                        process: Utility.getDistinctOptions(response, "processname"),
                        nextprocess: Utility.getDistinctOptions(response, "nextprocessname"),
                    };
                    const combinedFilterValues = {
                        user: uniqueFilterValues.user.map(value => {
                            const matchingItem = response.find(item => item.username === value);
                            return matchingItem ? `${matchingItem.userid} | ${value}` : value;
                        }),
                        department: uniqueFilterValues.department.map(value => {
                            const matchingItem = response.find(item => item.department_name === value);
                            return matchingItem ? `${matchingItem.department_id} | ${value}` : value;
                        }),
                        updatedby: uniqueFilterValues.updatedby.map(value => {
                            const matchingItem = response.find(item => item.updatedbyid === value);
                            return matchingItem ? `${value} | ${matchingItem.updatedbyusername}` : value;
                        }),
                        processname: uniqueFilterValues.process.map(value => {
                            const matchingItem = response.find(item => item.processname === value);
                            return matchingItem ? `${matchingItem.processid} | ${value}` : value;
                        }),
                        nextprocessname: uniqueFilterValues.nextprocess.map(value => {
                            const matchingItem = response.find(item => item.nextprocessname === value);
                            return matchingItem ? `${matchingItem.nextprocessid} | ${value}` : value;
                        }),
                        operation: uniqueFilterValues.operation,
                    };
                    const filterMap = generateFilterMap(Array.isArray(response) ? response : [response]);
                    prepareColumns(filterMap, combinedFilterValues);
                    setData(data);
                    setDetailLoader(false)
                } else {
                    setErrorSnackbar(true)
                    setDetailLoader(false)
                }
            }
        );
    };

    const generateFilterMap = (responseData) => {
        const filterMap = {};

        // Process issued department head
        const department = responseData.map((dept) => ({
            id: dept.next_department_head_id,
            username: dept.nextdepartmenthead,
        }));

        filterMap.nextdepartmenthead = [
            ...new Set(
                department.map((group) => `${group.id} | ${group.username}`)
            ),
        ];

        // Process issued department 
        const departmentGroup = responseData.map((dept) => ({
            id: dept.next_department_id,
            username: dept.nextdepartmentname,
        }));

        filterMap.nextdepartmentname = [
            ...new Set(
                departmentGroup.map((group) => `${group.id} | ${group.username}`)
            ),
        ];

        // Process process 
        const process = responseData.map((dept) => ({
            id: dept.processid,
            name: dept.processname,
        }));

        filterMap.processname = [
            ...new Set(
                process.map((group) => `${group.id} | ${group.name}`)
            ),
        ];

        // Process next process 
        const nextprocess = responseData.map((dept) => ({
            id: dept.nextprocessid,
            name: dept.nextprocessname,
        }));

        filterMap.nextprocessname = [
            ...new Set(
                nextprocess.map((group) => `${group.id} | ${group.name}`)
            ),
        ];

        return filterMap;
    };

    const prepareColumns = (filterMap, combinedFilterValues) => {
        let columnNames = [];
        const columnData = initialColumns(combinedFilterValues);

        columnData.forEach(iCol => {
            if (iCol.field === "nextdepartmenthead") {
                iCol.type = "singleSelect";
                iCol.valueOptions = filterMap ? filterMap.nextdepartmenthead : [];
                iCol.filterOperators = Utility.customFilters(filterMap, iCol.field, 'next_department_head_id', 'nextdepartmenthead');
            }
            if (iCol.field === "nextdepartmentname") {
                iCol.type = "singleSelect";
                iCol.valueOptions = filterMap ? filterMap.nextdepartmentname : [];
                iCol.filterOperators = Utility.customFilters(filterMap, iCol.field, 'next_department_id', 'nextdepartmentname');
            }
            if (iCol.field === "processname") {
                iCol.type = "singleSelect";
                iCol.valueOptions = filterMap ? filterMap.processname : [];
                iCol.filterOperators = Utility.customFilters(filterMap, iCol.field, 'processid', 'processname');
            }
            if (iCol.field === "nextprocessname") {
                iCol.type = "singleSelect";
                iCol.valueOptions = filterMap ? filterMap.nextprocessname : [];
                iCol.filterOperators = Utility.customFilters(filterMap, iCol.field, 'nextprocessid', 'nextprocessname');
            }
            columnNames.push(iCol);
        });

        setColumnNames(columnNames);
    }

    const handleRemarkModal = (params) => {
        setSourcedataData(params)
        setShowRemarkModal(true)
    }

    const handleEvaluationModal = () => {
        setShowEvaluationModal(true)
    }

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const handleBucketDetailModal = (params) => {
        setBucketId(params.value);
        setShowBucketDetailModal(true)
    };

    const genericCellValueWithColorClass = (currVal, prevVal) => {
        if (!currVal || (typeof currVal === "string" && currVal.trim() === ''))
            currVal = '';
        if (!prevVal || (typeof prevVal === "string" && prevVal.trim() === ''))
            prevVal = '';

        return currVal !== prevVal ? 'blueCell' : ""
    }

    const initialColumns = (filterValues) => [
        {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            width: 80,
        },
        {
            field: "stoneid",
            headerName: "STONEID",
            width: 80,
            headerAlign: "center",
            align: "center",
            type: "number",
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{ textDecorationLine: "underline" }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.stoneid, params.row.prevRow.stoneid)
        },
        {
            field: "operation",
            headerName: "OPERATION",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.operation : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.operation, params.row.prevRow.operation)
        },
        {
            field: "bucketid",
            headerName: "BUCKETID",
            headerAlign: "center",
            align: "center",
            minWidth: 100,
            flex: 1,
            type: "number",
            renderCell: (params) => (Utility.NeedToShowBucketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleBucketDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.bucketid, params.row.prevRow.bucketid)
        },
        {
            field: "createdfrompacket",
            headerName: "CREATED FROM PACKET",
            headerAlign: "center",
            align: "center",
            width: 180,
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.createdfrompacket, params.row.prevRow.createdfrompacket)
        },
        {
            field: "carats",
            headerName: "CARATS",
            headerAlign: "center",
            align: "center",
            minWidth: 100,
            flex: 1,
            type: "number",
            renderCell: renderCellExpand,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.carats, params.row.prevRow.carats)
        },
        {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price),
            cellClassName: (params) => genericCellValueWithColorClass(params.row.price, params.row.prevRow.price)
        },
        {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            minWidth: 180,
            flex: 1,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
            cellClassName: (params) => genericCellValueWithColorClass(params.row.createdat, params.row.prevRow.createdat)
        },
        {
            field: "updatedat",
            headerName: "UPDATED AT",
            headerAlign: "center",
            align: "center",
            minWidth: 180,
            flex: 1,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
            cellClassName: (params) => genericCellValueWithColorClass(params.row.updatedat, params.row.prevRow.updatedat)
        },
        {
            field: "userid",
            headerName: "USER",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.user : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => params.row.userid + " | " + params.row.username,
            renderCell: renderCellExpand,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.userid, params.row.prevRow.userid)
        },
        {
            field: "department_id",
            headerName: "DEPARTMENT",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.department : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => params.row.department_id + " | " + params.row.department_name,
            renderCell: renderCellExpand,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.department_id, params.row.prevRow.department_id)
        },
        {
            field: "nextdepartmenthead",
            headerName: "ISSUED DEPARTMENT HEAD",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                params.row.next_department_head_id ?
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.next_department_head_id}</Avatar>}
                            label={params.row.nextdepartmenthead}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box>
                    : '-'
            ),
            cellClassName: (params) => genericCellValueWithColorClass(params.row.next_department_head_id, params.row.prevRow.next_department_head_id),
            disableExport: true,
        },
        {
            field: "nextdepartmentname",
            headerName: "ISSUED DEPARTMENT NAME",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                params.row.next_department_id ?
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.next_department_id}</Avatar>}
                            label={params.row.nextdepartmentname}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box> : '-'
            ),
            cellClassName: (params) => genericCellValueWithColorClass(params.row.next_department_id, params.row.prevRow.next_department_id),
            disableExport: true,
        },
        {
            field: "processname",
            headerName: "PROCESS",
            headerAlign: "center",
            align: "center",
            width: 250,
            renderCell: (params) => {
                if (params.row.processid == null || params.row.processname == null)
                    return '-';
                return (
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.row.processid}</Avatar>}
                            label={params.row.processname}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box>
                )
            },
            cellClassName: (params) => genericCellValueWithColorClass(params.row.processid, params.row.prevRow.processid),
            disableExport: true,
        },
        {
            field: "nextprocessname",
            headerName: "NEXT PROCESS",
            headerAlign: "center",
            align: "center",
            width: 250,
            renderCell: (params) => {
                if (params.row.nextprocessid == null || params.row.nextprocessname == null)
                    return '-';
                return (
                    <Box sx={{ minHeight: "auto", mt: 1 }}>
                        <Chip
                            avatar={<Avatar>{params.row.nextprocessid}</Avatar>}
                            label={params.row.nextprocessname}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    </Box>
                )
            },
            cellClassName: (params) => genericCellValueWithColorClass(params.row.nextprocessid, params.row.prevRow.nextprocessid),
            disableExport: true,
        },
        {
            field: "updatedbyid",
            headerName: "UPDATED BY",
            headerAlign: "center",
            align: "center",
            minWidth: 150,
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.updatedby : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => params.row.updatedbyid + " | " + params.row.updatedbyusername,
            renderCell: renderCellExpand,
            cellClassName: (params) => genericCellValueWithColorClass(params.row.updatedbyid, params.row.prevRow.updatedbyid)
        },
        {
            field: "remark",
            headerName: "REMARK",
            headerAlign: "center",
            align: "center",
            flex: 2,
            minWidth: 350,
            renderCell: (params) => (
                params.row.remarks === null || params.row.remarks === "" ? '' :
                    showInlineRemark ?
                        <Box sx={{ minHeight: "auto" }}>
                            <table style={{ border: '1pt solid black' }}>
                                <tbody>

                                    <tr style={{ border: '1pt solid black' }}>
                                        <th style={{ border: '1pt solid black' }}>Remark Category</th>
                                        <th style={{ border: '1pt solid black' }}>Remarks</th>
                                    </tr>
                                    {
                                        Utility.GetRemark(params.row.remarks).map((iRow) =>
                                            <tr key={iRow.id} style={{ border: '1pt solid black' }}>
                                                <td style={{ border: '1pt solid black' }}>{iRow.category}</td>
                                                <td style={{ border: '1pt solid black' }}>{iRow.remark}</td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        </Box>
                        :
                        <Button
                            sx={{ width: 220 }}
                            variant="contained"
                            onClick={(e) => {
                                e.stopPropagation()
                                handleRemarkModal(params.row.remarks)
                            }}
                        >
                            Show Remark
                        </Button>
            ),
        },
        {
            field: "lossremark",
            headerName: "LOSS REMARK",
            headerAlign: "center",
            align: "center",
            flex: 2,
            minWidth: 350,
            renderCell: (params) => (
                params.row.lossremarks === null || params.row.lossremarks === "" ? '' :
                    showInlineRemark ?
                        <Box sx={{ minHeight: "auto" }}>
                            <table style={{ border: '1pt solid black' }}>
                                <tr style={{ border: '1pt solid black' }}>
                                    <th style={{ border: '1pt solid black' }}>Loss Category</th>
                                    <th style={{ border: '1pt solid black' }}>Loss Remarks</th>
                                </tr>
                                {
                                    Utility.GetRemark(params.row.lossremarks).map((iRow) =>
                                        <tr key={iRow.id} style={{ border: '1pt solid black' }}>
                                            <td style={{ border: '1pt solid black' }}>{iRow.category}</td>
                                            <td style={{ border: '1pt solid black' }}>{iRow.remark}</td>
                                        </tr>
                                    )
                                }
                            </table>
                        </Box>
                        :
                        <Button
                            sx={{ width: 220 }}
                            variant="contained"
                            onClick={(e) => {
                                e.stopPropagation()
                                handleRemarkModal(params.row.lossremarks)
                            }}
                        >
                            Show Loss Remark
                        </Button>
            ),
        },
    ];

    const rows = data;
    const columns = columnNames;
    return (
        <div>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            <Box>
                {showRemarkModal ? (
                    <RemarkModal
                        show={showRemarkModal}
                        sourceData={sourcedata}
                        handleClose={() => setShowRemarkModal(false)}
                    />
                ) : null}
                {showEvaluationModal ? (
                    <EvaluationModal
                        show={showEvaluationModal}
                        packetId={packetId}
                        kapanId={kapanId}
                        handleClose={() => setShowEvaluationModal(false)}
                    />
                ) : null}
                {showStoneDetailModal ? (
                    <StoneDetailModal
                        show={showStoneDetailModal}
                        stoneId={stoneId}
                        handleClose={() => setShowStoneDetailModal(false)}
                    />
                ) : null}
                {showKapanDetailModal ? (
                    <KapanHistoryModal
                        show={showKapanDetailModal}
                        kapanId={kapanId}
                        close={() => setShowKapanDetailModal(false)}
                    />
                ) : null}
                {showBucketDetailModal ? (
                    <BucketDetailModal
                        show={showBucketDetailModal}
                        bucketId={bucketId}
                        handleClose={() => setShowBucketDetailModal(false)}
                    />
                ) : null}
                <Modal show={show} onHide={close} dialogClassName="bhb" enforceFocus={false}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <img
                                src={logo}
                                height="40pt"
                                width="40pt"
                                alt={Constants.COMPANY_SITE}
                            />
                            &nbsp;&nbsp;Packet History
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Container maxWidth="xxl">
                            <Box style={{ display: 'flex', justifyContent: 'space-between', width: 400 }} >
                                <FormGroup sx={{ mt: 1, width: 200 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch

                                                checked={showInlineRemark}
                                                onChange={() => setShowInlineRemark(!showInlineRemark)}
                                            />
                                        }
                                        label="Inline Loss Remark"
                                    />
                                </FormGroup>

                                <Button
                                    sx={{ width: 200, height: 50 }}
                                    variant="contained"
                                    startIcon={<FaPen size={'15px'} />}
                                    onClick={(e) => {
                                        handleEvaluationModal()
                                    }}
                                >
                                    Show Evaluation
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    width: "100%",
                                    position: "relative",
                                    "& .MuiTablePagination-selectLabel": {
                                        marginTop: "15px",
                                    },
                                    "& .MuiTablePagination-displayedRows": {
                                        marginTop: "15px",
                                    },
                                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                                        color: "#1976d2",
                                        padding: "4px 5px",
                                        borderRadius: "4px",
                                    },
                                    "& .css-cc8tf1": {
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                                        color: "#1976d2",
                                        padding: "5px 5px",
                                        borderRadius: "4px",
                                    },
                                    "& .blueCell": {
                                        color: "#080808",
                                        backgroundColor: "#d4eff8",
                                    },
                                    height: "50vh",
                                }}
                            >
                                <DataGridPro
                                    disableRowSelectionOnClick={true}
                                    getRowHeight={() => "auto"}
                                    sx={{
                                        '& .MuiDataGrid-columnHeader': {
                                            color: '#fff',
                                            backgroundColor: 'grey',
                                        },
                                        "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                            py: "8px",
                                        },
                                        "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                            py: "15px",
                                        },
                                        "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                            py: "22px",
                                        },
                                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                                            width: "0.4em",
                                            height: "0.7em",
                                        },
                                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                                            background: "#f1f1f1",
                                        },
                                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                                            backgroundColor: "#888",
                                        },
                                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
                                            background: "#555",
                                        },
                                        fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                    }}
                                    density="compact"
                                    slots={{ toolbar: GridToolbar }}
                                    rows={rows}
                                    columns={columns}
                                    getRowId={(row) => row.id}
                                    initialState={{
                                        columns: {
                                            columnVisibilityModel: {
                                                // Hide columns id, the other columns will remain visible
                                                // locationid: false,
                                                // id: false,
                                            },
                                        },
                                        sorting: {
                                            sortModel: [{ field: "updatedat", sort: "desc" }],
                                        },
                                        pagination: {
                                            paginationModel: Constants.pageSize,
                                        },
                                    }}
                                    pageSizeOptions={Constants.pageSizeOptions}
                                    pagination
                                    slotProps={{
                                        toolbar: {
                                            showQuickFilter: true,
                                            quickFilterProps: { debounceMs: 500 },
                                        },
                                    }}
                                />
                                <Box style={{ position: "absolute", top: 5, right: "270px" }}>
                                    {detailLoader ? (
                                        <CircularProgress size="1rem" />
                                    ) : (
                                        <Tooltip title="Reload List">
                                            <RefreshOutlinedIcon
                                                onClick={() => handleSearch(kapanId, packetId)}
                                                color="primary"
                                                fontSize="medium"
                                                sx={{ cursor: "pointer", mr: 1 }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>

                        </Container>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="contained" color="error" onClick={close}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Box>
        </div>
    );
};

export default PacketHistoryModal;
